<div class="flex h-full overflow-hidden bg-white border-1 border-gray-200 rounded-xl px-8 py-4">
  <!-- ===== LEFT PANEL: Folder Tree ===== -->
  <div class="flex flex-col w-96 min-w-64  ">

    <!-- Header -->
    <div class="flex items-center justify-between px-4 py-3 h-10">
      <span
        class="text-sm font-semibold text-surface-700 uppercase tracking-wide">{{ 'entity.folders' | translate }}</span>
      <elcia-button (onClick)="openCreateFolderDialog()" [icon]="'plus'" [size]="'small'" [variant]="'text'"/>
    </div>
    <p-divider/>
    <!-- Tree scroll area -->
    <div class="flex-1 overflow-y-auto py-2">

      <!-- Folder nodes -->
      @for (node of flatFolderNodes(); track node.folder.id) {
        <div
          (click)="selectFolder(node.folder.id)"
          (dragend)="onFolderDragEnd()"
          (dragleave)="onFolderDragLeave()"
          (dragover)="onFolderDragOver($event, node.folder)"
          (dragstart)="onFolderDragStart($event, node.folder)"
          (drop)="onFolderDrop($event, node.folder)"
          [animate.enter]="'slide-in-top'"
          [class.bg-blue-50]="dragOverFolderId() === node.folder.id && dragPosition() === 'inside'"
          [class.bg-primary-50]="store.selectedFolderId() === node.folder.id"
          [class.font-medium]="store.selectedFolderId() === node.folder.id"
          [class.opacity-50]="draggedFolderId() === node.folder.id"
          [class.ring-2]="dragOverFolderId() === node.folder.id && dragPosition() === 'inside'"
          [class.ring-blue-300]="dragOverFolderId() === node.folder.id && dragPosition() === 'inside'"
          [class.text-primary-700]="store.selectedFolderId() === node.folder.id"
          [style.paddingLeft.px]="node.depth * 16 + 8"
          class="group flex items-center gap-1 mx-2 my-0.5 rounded-lg cursor-pointer transition-colors relative"
          draggable="true"
        >
          <!-- Drop position indicators -->
          @if (dragOverFolderId() === node.folder.id && dragPosition() === 'before') {
            <div class="absolute left-2 right-2 top-0 h-0.5 bg-blue-400 rounded-full pointer-events-none"></div>
          }
          @if (dragOverFolderId() === node.folder.id && dragPosition() === 'after') {
            <div class="absolute left-2 right-2 bottom-0 h-0.5 bg-blue-400 rounded-full pointer-events-none"></div>
          }
          <!-- Expand toggle -->
          <button
            (click)="toggleFolder(node.folder.id, $event)"
            class="w-5 h-5 flex items-center justify-center rounded text-surface-400 hover:text-surface-600 flex-shrink-0"
          >
            @if (node.hasChildren) {
              <elcia-expander-icon [isExpanded]="node.isExpanded" class="text-gray-400"/>
            }
          </button>

          <!-- Folder icon -->
          <i [class.pi-folder-open]="node.isExpanded || store.selectedFolderId() === node.folder.id"
             [class.pi-folder]="!node.isExpanded && store.selectedFolderId() !== node.folder.id"
             [class.text-primary-500]="store.selectedFolderId() === node.folder.id"
             [class.text-yellow-500]="store.selectedFolderId() !== node.folder.id"
             class="pi text-sm flex-shrink-0"
          ></i>

          <!-- Folder name -->
          <span class="flex-1 items-center flex gap-2 text-sm py-1.5 truncate pr-1">{{ node.folder.name }}
            @switch (node.folder.folderType) {
              @case (FolderType.TODO) {
                <elcia-icon [iconSize]="'sm'" [iconType]="'todo'"/>
              }
              @case (FolderType.INVOICES) {
                <elcia-icon [iconSize]="'sm'" [iconType]="'finances'"/>
              }
              @case (FolderType.CREDITNOTES) {
                <elcia-icon [iconSize]="'sm'" [iconType]="'credit-note'"/>
              }
              @case (FolderType.TIMESHEET) {
                <elcia-icon [iconSize]="'sm'" [iconType]="'clock'"/>
              }
            }
          </span>


          <!-- Action buttons (visible on hover) -->
          <div
            class="flex items-center opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
            <elcia-button
              (click)="$event.stopPropagation()"
              (onClick)="openCreateSubfolderDialog(node.folder)"
              [icon]="'plus'"
              [size]="'small'"
              [variant]="'text'"
            />
            <elcia-button
              (click)="$event.stopPropagation()"
              (onClick)="openEditFolderDialog(node.folder)"
              [icon]="'edit'"
              [size]="'small'"
              [variant]="'text'"
            />
            <elcia-button
              (click)="$event.stopPropagation()"
              (onClick)="deleteFolder(node.folder)"
              [icon]="'delete'"
              [severity]="'danger'"
              [size]="'small'"
              [variant]="'text'"
            />
          </div>

          <div
            class="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-4 rounded-r opacity-0 group-hover:opacity-100 bg-surface-300 transition-opacity"></div>
        </div>
      }
    </div>
  </div>
  <p-divider [layout]="'vertical'"/>
  <!-- ===== RIGHT PANEL: Documents ===== -->
  <div class="flex flex-col flex-1 overflow-hidden">

    <!-- Toolbar -->
    <div class="flex items-center justify-between px-6 py-3 h-10">
      <div class="flex items-center gap-1.5 text-sm">
        @if (store.selectedFolder()) {
          <elcia-avatar [avatars]="store.selectedFolder()?.userAccess || []" [maxItem]="15"/>
        }
      </div>

      @if (store.selectedFolderId() !== null) {
        <div class="flex items-center gap-4">
          <span
            class="text-xs text-surface-500">{{ store.documents().length }} {{ 'entity.documents' | translate }}</span>
          <elcia-button (onClick)="openFilePicker()" [icon]="'plus'" [size]="'small'"/>
        </div>
      }
    </div>
    <p-divider/>
    <!-- Documents area -->
    <div
      (dragenter)="onFileDragEnter($event)"
      (dragleave)="onFileDragLeave()"
      (dragover)="onFileDragOver($event)"
      (drop)="onFileDrop($event)"
      class="flex-1 flex flex-col overflow-hidden p-6 relative"
    >
      @if (isDraggingFile() && store.selectedFolderId() !== null) {
        <div
          class="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 m-3 rounded-2xl border-2 border-dashed border-primary-400 bg-primary-50/80">
          <i class="pi pi-cloud-upload text-4xl text-primary-400"></i>
          <p class="text-sm text-surface-500">{{ 'app.drop-files-here' | translate }}</p>
        </div>
      }
      @if (store.selectedFolderId() === null) {
        <div class="flex flex-col items-center justify-center h-full text-gray-500">
          <elcia-icon [iconSize]="'4xl'" [iconType]="'folder-open'"/>
          <p class="text-md">{{ 'app.select-folder-to-show-documents' |translate }}</p>
        </div>
      } @else if (store.isLoadingDocuments()) {
        <elcia-loader/>
      } @else if (store.documents().length === 0) {
        <div class="flex flex-col items-center justify-center h-full text-gray-500">
          <elcia-icon [iconSize]="'4xl'" [iconType]="'file'"/>
          <p class="text-md">{{ 'app.no-documents' | translate }}</p>
        </div>
      } @else {
        <div class="flex-1 overflow-y-auto flex flex-col gap-3">
          @for (doc of store.documents(); track doc.id) {
            <elcia-document-item
              (dragend)="onDocumentDragEnd()"
              (dragstart)="onDocumentDragStart($event, doc)"
              (onDelete)="deleteDocument(doc)"
              (onDownload)="downloadDocument(doc)"
              [animate.enter]="'slide-in-top'"
              [class.opacity-50]="draggedDocumentId() === doc.id"
              [document]="doc"
              class="hover:bg-primary-50 group flex items-center gap-4 px-4 py-3 rounded-xl border border-gray-200 bg-surface-0 hover:border-surface-300 hover:shadow-sm transition-all cursor-grab active:cursor-grabbing"
              draggable="true"
            />
          }
        </div>
      }
    </div>
  </div>
</div>

<!-- Folder Dialog (Create / Edit) -->
@if (showFolderDialog()) {
  <elcia-dialog
    (onAction)="onDialogSave()"
    (onClose)="closeFolderDialog()"
    [isActionLoading]="isFolderFormSaving()"
    [size]="'x-small'"
    [title]="folderDialogTitle()"
  >
    <elcia-folder-form
      (onSubmit)="onFolderFormSubmit($event)"
      [folderInitial]="folderDialogInitial()"
      [parentFolder]="folderDialogParent()"
    />
  </elcia-dialog>
}

<input #fileInput (change)="onFileInputChange($event)" class="hidden" multiple type="file"/>
